import { Box, keyframes, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header from '../components/Header';
import {
  FinalCTASection,
  FooterNew,
  PricingPreview,
} from '../components/organisms';
import { ProductHero, ProductHowItWorks } from '../components/product';
import SEO from '../components/SEO';
import { useAnalytics } from '../hooks/useAnalytics';

/**
 * Product page component for EllaFin
 *
 * Streamlined product page with focused sections:
 * 1. Product Hero - Split layout with bento preview and feature ticker
 * 2. How It Works - 3-step timeline
 * 3. Pricing Preview - Tier-based pricing cards
 * 4. Final CTA - Full-width call-to-action
 *
 * Target audience: CFOs, finance teams, FP&A, FinOps practitioners
 *
 * @returns {JSX.Element} Product page component
 */
function Product() {
  const theme = useTheme();
  const { trackCTA } = useAnalytics();

  /**
   * Handle primary CTA click
   * @param {string} location - Where the CTA was clicked
   */
  const handlePrimaryCTA = (location) => {
    trackCTA('Get Early Access', location, {
      destination: '/early-access',
    });
  };

  /**
   * Handle secondary CTA click
   * @param {string} location - Where the CTA was clicked
   */
  const _handleSecondaryCTA = (location) => {
    trackCTA('Secondary CTA', location, {
      destination: 'varies',
    });
  };

  return (
    <Box
      sx={{
        textAlign: 'left',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <SEO
        title="Product - AWS Cloud Cost Management for Finance Teams"
        description="Explore EllaFin's FinOps platform features including complete cost allocation, anomaly detection, AI-powered forecasting, automated financial workflows, and conversation analytics."
        path="/product"
        keywords="AWS cost allocation, cloud cost reporting, anomaly detection, financial workflows, AI forecasting, cost center mapping, FinOps tools"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'EllaFin FinOps Platform',
          description:
            'Finance-First FinOps platform with cost allocation, anomaly detection, AI-powered forecasting, and automated financial workflows for AWS.',
          url: 'https://www.ellafin.ai/product',
          brand: {
            '@type': 'Brand',
            name: 'EllaFin',
          },
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            availability: 'https://schema.org/PreOrder',
            offerCount: '3',
          },
        }}
      />

      {/* Navigation Header */}
      <Header />

      {/* Section 1: Product Hero (includes feature ticker) */}
      <ProductHero />

      {/* Section 2: Pricing Preview */}
      <PricingPreview
        sectionId="pricing"
        headline="Business Intelligence Delivered"
        tiers={[
          {
            name: 'Team',
            description:
              'FinOps practitioners, FP&A teams, and engineering leaders share the same intelligent view of AWS spend management and financial analysis. With Custom Chat Agents and enriched data sets, your team spends less time wrangling spreadsheets and more time making decisions that matter.',
            price: '',
            features: [
              'Standard cost optimization dashboards — pre-built views that surface savings opportunities and spending patterns automatically',
              '10 Finance-First interactive dashboards — purpose-built for financial decision-making',
              'Up to 5 data sources — enrich your cost and usage reports with your financial data',
              '3 workflows - automate a financial workflow for AWS spend reporting',
              '3 Chat Agents - your team defines the data, we create the source of truth, everyone gets answers they can verify and trust',
              '1 Integration – connect EllaFin™ into your current financial workflow',
              '5 included users',
            ],
            ctaText: 'Join Wait List',
            ctaHref: '/early-access',
          },
          {
            name: 'Individual',
            description:
              'Delivers a personal, finance-first analytics experience that aligns your AWS cost data with your current financial workflow and reports. Rather than handing you a tool and wishing you luck, we put ready-made insights in front of you from day one. Perfect for FinOps practitioners, finance leaders, and technical owners who own AWS spend management and reporting.',
            price: '',
            features: [
              'Standard cost optimization dashboards — pre-built views that surface savings opportunities and spending patterns automatically',
              '5 Finance-First interactive dashboards — purpose-built for financial decision-making',
              'Up to 3 data sources — enrich your cost and usage reports with your financial data',
              '1 workflow - automate a financial workflow for AWS spend reporting',
              '1 included user',
            ],
            ctaText: 'Get Early Access',
            ctaHref: '/early-access',
            highlighted: true,
            badge: 'Early Access',
          },
          {
            name: 'Department',
            description:
              'FP&A leaders and finance teams need the depth to account for, report on, and forecast cloud costs with the same rigor as every other line item. Integrate variable AWS spend into your existing financial structure and reporting.',
            price: '',
            features: [
              'Standard cost optimization dashboards — pre-built views that surface savings opportunities and spending patterns automatically',
              '10 Finance-First interactive dashboards — purpose-built for financial decision-making',
              'Up to 5 data sources — enrich your cost and usage reports with your financial data',
              '3 workflows — automate a financial workflow for AWS spend reporting',
              '3 Chat Agents — your team defines the source of truth, everyone gets answers they can verify and trust',
              'Advanced Automation — multi-step workflows with 3rd party systems',
              '2 integrations — connect EllaFin™ into your current financial workflows',
              "1 Power User Access — build custom agents and automated workflows tailored to your department's needs",
              '15 included users',
            ],
            ctaText: 'Join Wait List',
            ctaHref: '/early-access',
          },
        ]}
      />

      {/* Feature Ticker Strip */}
      <TickerStrip />

      {/* Section 3: How It Works (3-step) */}
      <ProductHowItWorks sectionId="how-it-works" />

      {/* Section 4: Final CTA Block */}
      <FinalCTASection
        sectionId="final-cta"
        headline="Ready to Speak AWS and FP&A?"
        subheadline="Join the early access program today"
        primaryCtaText="Get Early Access"
        primaryCtaHref="/early-access"
        onPrimaryCtaClick={() => handlePrimaryCTA('ProductFinalCTA')}
      />

      {/* Footer */}
      <FooterNew />
    </Box>
  );
}

/** Ticker scroll animation */
const tickerScrollAnimation = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

/** Ticker phrases */
/** @type {Array<{type: 'text', value: string} | {type: 'logo'}>} */
const tickerItems = [
  { type: 'text', value: 'AI for FinOps, FinOps for AI' },
  { type: 'text', value: 'Anomaly Detection' },
  { type: 'logo' },
  { type: 'text', value: 'Automated Financial Workflows' },
  { type: 'text', value: 'Intelligent Forecasting' },
  { type: 'logo' },
  { type: 'text', value: 'One Platform, Financial Insights for All' },
  { type: 'text', value: 'Complete Cost Allocation' },
  { type: 'text', value: 'Conversation Analytics' },
  { type: 'logo' },
  { type: 'text', value: 'AI Powered Variance Analysis' },
];

/** Feature ticker strip component */
function TickerStrip() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        overflow: 'hidden',
        backgroundColor: '#1e2140',
        py: { xs: 2.5, md: 3.5 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: 'fit-content',
          animation: `${tickerScrollAnimation} 40s linear infinite`,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          '&:hover': {
            animationPlayState: 'paused',
          },
        }}
      >
        {[0, 1].map((set) => (
          <Box
            key={set}
            sx={{
              display: 'flex',
              gap: 6,
              pr: 6,
              alignItems: 'center',
            }}
          >
            {tickerItems.map((item, index) =>
              item.type === 'logo'
                ? <Box
                    key={`ticker-${set}-${index}`}
                    component="img"
                    src={`${process.env.PUBLIC_URL}/images/elephant-icon-white-green.png`}
                    alt="EllaFin™"
                    sx={{
                      height: { xs: 28, md: 32 },
                      width: 'auto',
                      borderRadius: '4px',
                      opacity: 0.9,
                    }}
                  />
                : <Typography
                    key={`ticker-${set}-${index}`}
                    variant="body2"
                    component="span"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      letterSpacing: '0.02em',
                      whiteSpace: 'nowrap',
                      opacity: 0.8,
                    }}
                  >
                    {item.value.split(/(Fin)/g).map((part, i) =>
                      part === 'Fin'
                        ? <span
                            key={i}
                            style={{ color: theme.palette.secondary.main }}
                          >
                            {part}
                          </span>
                        : part
                    )}
                  </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default Product;
